import {AppState, NativeEventEmitter, NativeModules, Platform} from 'react-native';

/**
 * Android incoming-call notification (native module `HappyIncomingCall`).
 *
 * react-native-callkeep runs SELF-MANAGED on Android: Telecom shows no incoming
 * UI and plays no ringtone — it only emits `showIncomingCallUi` and the app must
 * post its own CATEGORY_CALL notification (ringtone, full-screen intent over the
 * lock screen, Answer/Decline). This wrapper drives that notification and routes
 * the user's notification action (answer / decline / full-screen "show") back
 * into the existing CallKeep answer/end path.
 *
 * Android only; every function is a safe no-op on iOS/web and on binaries that
 * predate the native module (old builds never crash).
 */

const NATIVE_NAME = 'HappyIncomingCall';
const ACTION_EVENT = 'HappyIncomingCallAction';

/** Fallback delay before posting without CallKeep's `showIncomingCallUi` event. */
export const SHOW_FALLBACK_MS = 500;

const shownUuids = new Set();
const fallbackTimers = new Map();
const routedActions = new Set();

/** @returns {object|null} The native module (Android builds that ship it), else null. */
function getNative() {
    if (Platform.OS !== 'android') {
        return null;
    }

    return NativeModules[NATIVE_NAME] || null;
}

/** @returns {boolean} Whether the native incoming-call notification is available. */
export function isIncomingCallNotificationAvailable() {
    return !!getNative();
}

/**
 * Post the ringing call notification once per call UUID (deduped).
 * @param {{callUUID: string, callId?: string, callerName?: string, handle?: string,
 *          incomingLabel?: string, answerLabel?: string, declineLabel?: string, channelName?: string}} payload
 * @returns {boolean} Whether a show was requested (false: duplicate, missing uuid or no module).
 */
export function showIncomingCallNotification(payload = {}) {
    const native = getNative();
    const uuid = payload && payload.callUUID ? String(payload.callUUID) : '';
    if (!native || !uuid || shownUuids.has(uuid)) {
        return false;
    }
    shownUuids.add(uuid);
    clearFallback(uuid);
    try {
        const result = native.show({...payload, callUUID: uuid});
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (_error) {
        shownUuids.delete(uuid);

        return false;
    }

    return true;
}

/**
 * Post the notification after `delayMs` unless CallKeep's `showIncomingCallUi`
 * already did (killed/headless start: the event may never reach JS).
 * @param {object} payload Same as {@link showIncomingCallNotification}.
 * @param {number} [delayMs] Delay.
 * @returns {boolean} Whether a fallback was armed.
 */
export function scheduleIncomingCallFallback(payload = {}, delayMs = SHOW_FALLBACK_MS) {
    const uuid = payload && payload.callUUID ? String(payload.callUUID) : '';
    if (!getNative() || !uuid || shownUuids.has(uuid) || fallbackTimers.has(uuid)) {
        return false;
    }
    const timer = globalThis.setTimeout(() => {
        fallbackTimers.delete(uuid);
        showIncomingCallNotification(payload);
    }, delayMs);
    fallbackTimers.set(uuid, timer);

    return true;
}

function clearFallback(uuid) {
    const timer = fallbackTimers.get(uuid);
    if (timer) {
        globalThis.clearTimeout(timer);
        fallbackTimers.delete(uuid);
    }
}

/**
 * Stop ringing + remove the notification and drop the lock-screen override.
 * @param {string} [callUUID] Call UUID (omit = the last one shown).
 * @returns {void}
 */
export function cancelIncomingCallNotification(callUUID) {
    const uuid = callUUID ? String(callUUID) : null;
    if (uuid) {
        clearFallback(uuid);
        shownUuids.delete(uuid);
    }
    const native = getNative();
    if (!native) {
        return;
    }
    try {
        const result = native.cancel(uuid);
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (_error) {
        /* noop */
    }
}

/**
 * Android 14+ lets the user revoke full-screen intents. Everything else → true.
 * @returns {Promise<boolean>}
 */
export async function canUseFullScreenIntent() {
    const native = getNative();
    if (!native || typeof native.canUseFullScreenIntent !== 'function') {
        return true;
    }
    try {
        return (await native.canUseFullScreenIntent()) !== false;
    } catch (_error) {
        return true;
    }
}

/**
 * Open the system "full-screen notifications" setting for this app.
 * @returns {Promise<boolean>} Whether a settings screen opened.
 */
export async function openFullScreenIntentSettings() {
    const native = getNative();
    if (!native || typeof native.openFullScreenIntentSettings !== 'function') {
        return false;
    }
    try {
        return !!(await native.openFullScreenIntentSettings());
    } catch (_error) {
        return false;
    }
}

/**
 * Read + clear the notification action latched natively (answer/decline/show).
 * @returns {Promise<({callUUID: string, callId: (string|null), callerName: string, handle: (string|null), action: string}|null)>}
 */
export async function consumeLaunchAction() {
    const native = getNative();
    if (!native || typeof native.consumeLaunchAction !== 'function') {
        return null;
    }
    try {
        const latched = await native.consumeLaunchAction();

        return latched && latched.callUUID && latched.action ? latched : null;
    } catch (_error) {
        return null;
    }
}

/**
 * Route one latched action to the app's handlers (deduped per uuid+action).
 * @param {object|null} launch Result of {@link consumeLaunchAction}.
 * @param {{onAnswer?: Function, onDecline?: Function, onShow?: Function}} handlers Handlers.
 * @returns {string|null} The routed action, or null.
 */
export function routeLaunchAction(launch, handlers = {}) {
    if (!launch || !launch.callUUID || !launch.action) {
        return null;
    }
    const key = `${launch.callUUID}:${launch.action}`;
    if (routedActions.has(key)) {
        return null;
    }
    routedActions.add(key);
    const handler = {
        answer: handlers.onAnswer,
        decline: handlers.onDecline,
        show: handlers.onShow,
    }[launch.action];
    if (typeof handler !== 'function') {
        return null;
    }
    if (launch.action !== 'show') {
        cancelIncomingCallNotification(launch.callUUID);
    }
    try {
        handler(launch);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[voip] incoming-call action failed:', error?.message || error);
    }

    return launch.action;
}

/**
 * Consume + route latched actions now, on every native "action available" event
 * and whenever the app becomes active (covers cold starts and warm taps).
 * @param {{onAnswer?: Function, onDecline?: Function, onShow?: Function}} handlers Handlers.
 * @returns {() => void} Unsubscribe.
 */
export function startIncomingCallActionRouting(handlers) {
    const native = getNative();
    if (!native) {
        return () => {};
    }
    const drain = () => consumeLaunchAction().then((launch) => routeLaunchAction(launch, handlers)).catch(() => null);
    const subs = [];
    try {
        subs.push(new NativeEventEmitter(native).addListener(ACTION_EVENT, drain));
    } catch (_error) {
        /* old binary without the event: AppState + initial drain still work */
    }
    subs.push(AppState.addEventListener('change', (state) => {
        if (state === 'active') {
            drain();
        }
    }));
    drain();

    return () => subs.forEach((sub) => sub && typeof sub.remove === 'function' && sub.remove());
}

/** Test-only: reset module state. */
export function resetIncomingCallNotificationState() {
    fallbackTimers.forEach((timer) => globalThis.clearTimeout(timer));
    fallbackTimers.clear();
    shownUuids.clear();
    routedActions.clear();
}
